import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "../../components/ui/sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ConfirmDialog } from "../../components/admin/ConfirmDialog";
import { formatDate, formatDateTime } from "../../lib/dateUtils";

const REPORT_TYPE_LABELS = {
  inappropriate_behavior: "Inappropriate Behavior", consent_boundary_issue: "Consent / Boundary Issue",
  harassment: "Harassment", photographer_conduct: "Photographer Conduct", participant_conduct: "Participant Conduct",
  no_show: "No Show", unsafe_location: "Unsafe Location", discrimination: "Discrimination",
  payment_scam_concern: "Payment / Scam Concern", other: "Other",
};
const STATUSES = ["new", "reviewing", "awaiting_information", "action_required", "resolved", "closed"];
const SEVERITIES = ["low", "medium", "high", "critical"];

export default function SafetyReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [note, setNote] = useState("");
  const [statusForm, setStatusForm] = useState({ status: "", resolution: "" });
  const [action, setAction] = useState(null);
  const [contactForm, setContactForm] = useState({ user_id: "", subject: "", content: "" });
  const [confirmAction, setConfirmAction] = useState(null);
  const [suspendReason, setSuspendReason] = useState("");
  const [restrictReason, setRestrictReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get(`/admin/safety/${id}`).then(({ data }) => {
      setReport(data);
      setStatusForm({ status: data.status, resolution: data.resolution || "" });
    });
  };

  useEffect(() => {
    load();
    api.get("/admin/users").then(({ data }) => setAdmins(data.filter((u) => u.role === "admin" || u.role === "super_admin")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const runAction = async (fn, successMsg) => {
    setSubmitting(true);
    try {
      await fn();
      toast.success(successMsg);
      load();
      setAction(null);
      setConfirmAction(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const saveStatus = () =>
    runAction(() => api.put(`/admin/safety/${id}/status`, statusForm), "Status updated");

  const saveSeverity = (severity) =>
    runAction(() => api.put(`/admin/safety/${id}/severity`, null, { params: { severity } }), "Severity updated");

  const assign = (admin_id) => runAction(() => api.put(`/admin/safety/${id}/assign`, { admin_id }), "Assigned");

  const addNote = () => runAction(() => api.post(`/admin/safety/${id}/notes`, { note }).then(() => setNote("")), "Note added");

  const sendContact = () =>
    runAction(() => api.post(`/admin/safety/${id}/contact`, contactForm).then(() => setContactForm({ user_id: "", subject: "", content: "" })), "Participant contacted");

  const doSuspend = (user_id) =>
    runAction(() => api.post(`/admin/safety/${id}/suspend-user`, { user_id, reason: suspendReason }), "User suspended");

  const doRestrict = (user_id, restricted) =>
    runAction(() => api.post(`/admin/safety/${id}/restrict-booking`, { user_id, restricted, reason: restrictReason }), restricted ? "Booking access restricted" : "Booking access restored");

  if (!report) return <p className="font-medium text-midnight-black/60" data-testid="safety-detail-loading">Loading...</p>;

  return (
    <div data-testid="safety-report-detail" className="max-w-3xl">
      <button onClick={() => navigate("/admin/safety")} data-testid="back-to-safety" className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-midnight-black/60 hover:text-stranger-orange mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Safety
      </button>

      <h2 className="font-extrabold uppercase text-2xl mb-1">{REPORT_TYPE_LABELS[report.report_type] || report.report_type}</h2>
      <p className="text-sm font-medium text-midnight-black/60 mb-8">
        Reported by {report.reporter_name || report.reporter_role} ({report.reporter_role}) on {formatDate(report.created_at)}
        {report.session_title && ` · ${report.session_title}`}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
        <div className="border border-soft-gray rounded-xl p-4">
          <p className="text-[10px] font-bold uppercase text-midnight-black/50 mb-2">Status</p>
          <Select value={statusForm.status} onValueChange={(v) => setStatusForm({ ...statusForm, status: v })}>
            <SelectTrigger data-testid="safety-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
          {(statusForm.status === "resolved" || statusForm.status === "closed") && (
            <Textarea data-testid="safety-resolution-input" placeholder="Resolution (required to resolve/close)" value={statusForm.resolution} onChange={(e) => setStatusForm({ ...statusForm, resolution: e.target.value })} className="mt-3" />
          )}
          <Button data-testid="save-status-button" onClick={saveStatus} disabled={submitting} className="mt-3 w-full bg-midnight-black text-clean-white font-bold rounded-full">SAVE STATUS</Button>
        </div>

        <div className="border border-soft-gray rounded-xl p-4">
          <p className="text-[10px] font-bold uppercase text-midnight-black/50 mb-2">Severity</p>
          <Select value={report.severity} onValueChange={saveSeverity}>
            <SelectTrigger data-testid="safety-severity-select"><SelectValue /></SelectTrigger>
            <SelectContent>{SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-[10px] font-bold uppercase text-midnight-black/50 mb-2 mt-4">Assigned Admin</p>
          <Select value={report.assigned_admin_id || "none"} onValueChange={(v) => v !== "none" && assign(v)}>
            <SelectTrigger data-testid="safety-assign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
            <SelectContent>
              {admins.map((a) => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border border-soft-gray rounded-xl p-4 mb-8">
        <p className="text-[10px] font-bold uppercase text-midnight-black/50 mb-2">Description</p>
        <p className="text-sm font-medium mb-3" data-testid="safety-description">{report.description}</p>
        {report.incident_datetime && <p className="text-xs text-midnight-black/60">Incident time: {report.incident_datetime}</p>}
        {report.reported_user_name && <p className="text-xs text-midnight-black/60 mt-1">Reported user: {report.reported_user_name} ({report.reported_user_role})</p>}
        {report.evidence_urls?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="safety-evidence-list">
            {report.evidence_urls.map((url) => (
              <a key={url} href={url} target="_blank" rel="noreferrer" className="text-xs font-bold text-stranger-orange underline">Evidence file</a>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="safety-actions-bar">
        <button data-testid="safety-action-contact" onClick={() => setAction("contact")} className="text-[11px] font-bold uppercase tracking-wide px-4 py-2.5 rounded-full border-2 border-soft-gray hover:border-stranger-orange hover:text-stranger-orange">
          Contact Participant
        </button>
        <button data-testid="safety-action-suspend" onClick={() => setAction("suspend")} className="text-[11px] font-bold uppercase tracking-wide px-4 py-2.5 rounded-full border-2 border-red-200 text-red-600 hover:bg-red-50">
          Suspend User
        </button>
        <button data-testid="safety-action-restrict" onClick={() => setAction("restrict")} className="text-[11px] font-bold uppercase tracking-wide px-4 py-2.5 rounded-full border-2 border-red-200 text-red-600 hover:bg-red-50">
          Remove Booking Access
        </button>
      </div>

      <div className="mb-10">
        <h3 className="font-extrabold uppercase text-sm mb-4">Internal Notes</h3>
        <div className="flex gap-3 mb-4">
          <Textarea data-testid="safety-note-input" placeholder="Add an internal note..." value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <Button data-testid="add-note-button" onClick={addNote} disabled={!note.trim() || submitting} className="mb-6 bg-midnight-black text-clean-white font-bold rounded-full">ADD NOTE</Button>
        {report.internal_notes?.length === 0 ? (
          <p className="text-sm font-medium text-midnight-black/60" data-testid="no-safety-notes">No internal notes yet.</p>
        ) : (
          <div className="space-y-2" data-testid="safety-notes-list">
            {report.internal_notes?.map((n, i) => (
              <div key={i} className="border border-soft-gray rounded-xl p-4 text-sm">
                <p>{n.note}</p>
                <p className="text-xs text-midnight-black/40 mt-1">{n.admin_name} · {formatDateTime(n.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!action} onOpenChange={(v) => !v && setAction(null)}>
        <DialogContent data-testid="safety-action-modal">
          {action === "contact" && (
            <>
              <DialogHeader><DialogTitle className="font-extrabold uppercase">Contact Participant</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Button type="button" variant="outline" data-testid="contact-select-reporter" onClick={() => setContactForm({ ...contactForm, user_id: report.reporter_id })} className="rounded-full text-xs font-bold">Reporter</Button>
                  {report.reported_user_id && (
                    <Button type="button" variant="outline" data-testid="contact-select-reported" onClick={() => setContactForm({ ...contactForm, user_id: report.reported_user_id })} className="rounded-full text-xs font-bold">Reported User</Button>
                  )}
                </div>
                <Input data-testid="contact-user-id-input" placeholder="User ID" value={contactForm.user_id} onChange={(e) => setContactForm({ ...contactForm, user_id: e.target.value })} />
                <Input data-testid="contact-subject-input" placeholder="Subject" value={contactForm.subject} onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })} />
                <Textarea data-testid="contact-content-input" placeholder="Message" value={contactForm.content} onChange={(e) => setContactForm({ ...contactForm, content: e.target.value })} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAction(null)} className="rounded-full font-bold">Cancel</Button>
                <Button data-testid="contact-send-button" disabled={submitting || !contactForm.user_id || !contactForm.subject || !contactForm.content} onClick={sendContact} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full">SEND</Button>
              </DialogFooter>
            </>
          )}

          {action === "suspend" && (
            <>
              <DialogHeader><DialogTitle className="font-extrabold uppercase">Suspend a User</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Button type="button" variant="outline" data-testid="suspend-select-reporter" onClick={() => setContactForm({ ...contactForm, user_id: report.reporter_id })} className="rounded-full text-xs font-bold">Reporter ({report.reporter_id})</Button>
                  {report.reported_user_id && (
                    <Button type="button" variant="outline" data-testid="suspend-select-reported" onClick={() => setContactForm({ ...contactForm, user_id: report.reported_user_id })} className="rounded-full text-xs font-bold">Reported User</Button>
                  )}
                </div>
                <Input data-testid="suspend-user-id-input" placeholder="User ID to suspend" value={contactForm.user_id} onChange={(e) => setContactForm({ ...contactForm, user_id: e.target.value })} />
                <Textarea data-testid="suspend-reason-input" placeholder="Reason (required)" value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAction(null)} className="rounded-full font-bold">Cancel</Button>
                <Button data-testid="suspend-confirm-open-button" disabled={!contactForm.user_id || !suspendReason.trim()} onClick={() => setConfirmAction("suspend")} className="bg-red-600 hover:bg-red-700 text-clean-white font-bold rounded-full">SUSPEND</Button>
              </DialogFooter>
            </>
          )}

          {action === "restrict" && (
            <>
              <DialogHeader><DialogTitle className="font-extrabold uppercase">Remove Future Booking Access</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Button type="button" variant="outline" data-testid="restrict-select-reporter" onClick={() => setContactForm({ ...contactForm, user_id: report.reporter_id })} className="rounded-full text-xs font-bold">Reporter</Button>
                  {report.reported_user_id && (
                    <Button type="button" variant="outline" data-testid="restrict-select-reported" onClick={() => setContactForm({ ...contactForm, user_id: report.reported_user_id })} className="rounded-full text-xs font-bold">Reported User</Button>
                  )}
                </div>
                <Input data-testid="restrict-user-id-input" placeholder="User ID" value={contactForm.user_id} onChange={(e) => setContactForm({ ...contactForm, user_id: e.target.value })} />
                <Textarea data-testid="restrict-reason-input" placeholder="Reason (required)" value={restrictReason} onChange={(e) => setRestrictReason(e.target.value)} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAction(null)} className="rounded-full font-bold">Cancel</Button>
                <Button data-testid="restrict-confirm-open-button" disabled={!contactForm.user_id || !restrictReason.trim()} onClick={() => setConfirmAction("restrict")} className="bg-red-600 hover:bg-red-700 text-clean-white font-bold rounded-full">REMOVE ACCESS</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmAction === "suspend"}
        onOpenChange={(v) => !v && setConfirmAction(null)}
        title="Suspend this user?"
        description="They will lose access to Stranger Sessions immediately. This is a critical action."
        confirmLabel="YES, SUSPEND"
        destructive
        onConfirm={() => doSuspend(contactForm.user_id)}
      />
      <ConfirmDialog
        open={confirmAction === "restrict"}
        onOpenChange={(v) => !v && setConfirmAction(null)}
        title="Remove this user's future booking access?"
        description="They'll keep their account but won't be able to book new sessions. This is a critical action."
        confirmLabel="YES, REMOVE ACCESS"
        destructive
        onConfirm={() => doRestrict(contactForm.user_id, true)}
      />
    </div>
  );
}
